/**
 * <sticky-add-to-cart>
 *
 * Shows a sticky bar once the real "Add to cart" button scrolls out of view.
 * It never re-implements add-to-cart or variant-selection logic:
 *  - Its variant dropdowns live inside their own <variant-selects> element,
 *    built with the exact same option-value data as the main picker. Picking
 *    a value fires the theme's own native variant-selects "change" handling
 *    — the same code path the main picker uses — so price/availability/the
 *    hidden variant id all update correctly with zero custom logic here.
 *  - Its Add to cart button just clicks the real button.
 * Price, button state, and the sticky dropdowns themselves are kept in sync
 * with the main product form live via MutationObserver / the theme's
 * pub-sub bus, so everything stays correct through variant changes made
 * either place.
 */
class StickyAddToCart extends HTMLElement {
  constructor() {
    super();

    this.targetButton = document.getElementById(this.dataset.targetButtonId);
    this.mainPrice = document.getElementById(this.dataset.priceId);

    this.ownButton = this.querySelector('.sticky-add-to-cart__submit');
    this.ownButtonText = this.ownButton?.querySelector('span');
    this.ownSpinner = this.querySelector('.loading__spinner');
    this.priceSlot = this.querySelector('.sticky-add-to-cart__price');
    this.ownVariantSelects = this.querySelector('variant-selects');
    this.ownSelects = Array.from(this.querySelectorAll('.sticky-add-to-cart__select'));

    if (!this.targetButton || !this.ownButton) return;

    // The <option> ids are cloned from the same product-variant-options
    // markup as the main picker, so drop them here to avoid duplicate ids
    // elsewhere on the page. Nothing in this bar depends on them.
    this.querySelectorAll('option[id]').forEach((option) => option.removeAttribute('id'));

    this.onSubmitClick = this.onSubmitClick.bind(this);
    this.syncButtonState = this.syncButtonState.bind(this);
    this.syncPrice = this.syncPrice.bind(this);
    this.onOptionValueSelectionChange = this.onOptionValueSelectionChange.bind(this);

    this.ownButton.addEventListener('click', this.onSubmitClick);

    this.syncButtonState();
    this.syncPrice();

    new MutationObserver(this.syncButtonState).observe(this.targetButton, {
      attributes: true,
      attributeFilter: ['disabled', 'aria-disabled', 'class'],
      childList: true,
      subtree: true,
    });

    if (this.mainPrice) {
      new MutationObserver(this.syncPrice).observe(this.mainPrice, {
        childList: true,
        subtree: true,
        characterData: true,
      });
    }

    if (this.ownSelects.length && typeof subscribe === 'function' && typeof PUB_SUB_EVENTS === 'object') {
      this.unsubscribeOptionChange = subscribe(
        PUB_SUB_EVENTS.optionValueSelectionChange,
        this.onOptionValueSelectionChange
      );
    }

    this.observeVisibility();
  }

  disconnectedCallback() {
    this.unsubscribeOptionChange?.();
  }

  // --- Add to cart: delegate to the real button ---

  onSubmitClick() {
    if (this.targetButton.disabled || this.targetButton.getAttribute('aria-disabled') === 'true') return;
    this.ownButton.classList.add('loading');
    this.ownSpinner?.classList.remove('hidden');
    this.targetButton.click();
  }

  syncButtonState() {
    const isDisabled = this.targetButton.disabled || this.targetButton.getAttribute('aria-disabled') === 'true';
    this.ownButton.toggleAttribute('disabled', this.targetButton.disabled);
    this.ownButton.setAttribute('aria-disabled', isDisabled);

    const isLoading = this.targetButton.classList.contains('loading');
    this.ownButton.classList.toggle('loading', isLoading);
    this.ownSpinner?.classList.toggle('hidden', !isLoading);

    const mainText = this.targetButton.querySelector('span');
    if (this.ownButtonText && mainText && mainText.textContent.trim()) {
      this.ownButtonText.textContent = mainText.textContent.trim();
    }
  }

  // --- Price: mirror the real price element's markup live ---

  syncPrice() {
    if (this.mainPrice && this.priceSlot) {
      this.priceSlot.innerHTML = this.mainPrice.innerHTML;
    }
  }

  // --- Variant dropdowns: reflect changes made via the main picker (or any
  // other variant-selects instance) without re-triggering our own pipeline. ---

  onOptionValueSelectionChange({ data: { event, target } }) {
    if (!target || !this.ownVariantSelects) return;
    // A change that originated from our own dropdowns needs no sync back.
    if (this.ownVariantSelects.contains(event.target)) return;

    const optionName = target.dataset?.optionName;
    if (!optionName) return;

    const mine = this.ownSelects.find((select) => {
      const option = Array.from(select.options).find((opt) => opt.dataset.optionName === optionName);
      return Boolean(option);
    });
    if (mine && mine.value !== target.value) mine.value = target.value;
  }

  observeVisibility() {
    if (!('IntersectionObserver' in window)) {
      this.classList.add('is-visible');
      return;
    }

    new IntersectionObserver(
      ([entry]) => {
        const scrolledPastGoingDown = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        this.classList.toggle('is-visible', scrolledPastGoingDown);
      },
      { threshold: 0 }
    ).observe(this.targetButton);
  }
}

if (!customElements.get('sticky-add-to-cart')) {
  customElements.define('sticky-add-to-cart', StickyAddToCart);
}
